import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useFavorites } from "@/hooks/useFavorites";
import { tags } from "@/mocks/tags";
import { videos } from "@/mocks/videos";
import { formatDate, formatViews } from "@/utils/format";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import VideoPlayer from "@/pages/video/components/VideoPlayer";
import RelatedVideos from "@/pages/video/components/RelatedVideos";

export default function VideoDetailPage() {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const video = videos.find((item) => item.id === videoId);

  useDocumentTitle(
    video ? `${video.title} - 啪啪视频` : "视频不存在 - 啪啪视频",
    video
      ? `${video.description.slice(0, 60)}... 在啪啪视频在线观看《${video.title}》高清视频。`
      : "你访问的视频不存在或已被移除，返回首页继续浏览更多高清视频。",
    video ? `${video.title},${video.categoryName}视频,在线视频,啪啪视频` : "在线视频,啪啪视频",
  );

  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2500);
  };

  if (!video) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-warning-line"
          title="抱歉，这个视频不存在"
          description="该视频可能已被下架或链接有误。你可以返回首页，继续浏览其他精彩的高清视频。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const link = window.location.href;
    try {
      await navigator.clipboard.writeText(link);
      showToast("视频链接已复制，去分享给朋友吧");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  const tagItems = video.tags
    .map((slug) => tags.find((tag) => tag.slug === slug))
    .filter((tag): tag is { slug: string; name: string; description: string } => Boolean(tag));

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: video.categoryName, to: `/category/${video.category}` },
          { label: video.title },
        ]}
      />

      <div className="mt-4 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <VideoPlayer video={video} />

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h1 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              {video.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-user-3-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800">{video.author}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {formatDate(video.uploadDate)}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line"></i>
                {video.durationLabel}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)} 次播放
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {video.rating.toFixed(1)} 分
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-map-pin-line"></i>
                {video.region}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
              <button
                type="button"
                onClick={() => {
                  toggleFavorite(video.id);
                  showToast(favorited ? "已取消收藏" : "已加入收藏");
                }}
                aria-pressed={favorited}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 text-foreground-700 hover:border-accent-300 hover:text-accent-700"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line text-base"></i>
                分享
              </button>
              <Link
                to={`/category/${video.category}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-grid-line text-base"></i>
                更多{video.categoryName}视频
              </Link>
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <div className="mb-3 flex flex-wrap gap-2">
                {tagItems.map((tag) => (
                  <Link
                    key={tag.slug}
                    to={`/tag/${tag.slug}`}
                    className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors duration-200 hover:bg-secondary-200"
                  >
                    # {tag.name}
                  </Link>
                ))}
              </div>
              <h2 className="mb-2 font-heading text-sm font-semibold text-foreground-900">视频简介</h2>
              <p className="text-sm leading-relaxed text-foreground-600">{video.description}</p>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-information-line text-primary-500"></i>
              视频信息
            </h2>
            <dl className="flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">上传作者</dt>
                <dd className="font-medium text-foreground-800">{video.author}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">所属分类</dt>
                <dd>
                  <Link to={`/category/${video.category}`} className="font-medium text-primary-600 hover:text-primary-700">
                    {video.categoryName}
                  </Link>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">上传时间</dt>
                <dd className="font-medium text-foreground-800">{formatDate(video.uploadDate)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">视频时长</dt>
                <dd className="font-medium text-foreground-800">{video.durationLabel}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">播放次数</dt>
                <dd className="font-medium text-foreground-800">{formatViews(video.views)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">综合评分</dt>
                <dd className="font-medium text-foreground-800">{video.rating.toFixed(1)} / 5.0</dd>
              </div>
            </dl>
            <div className="mt-5 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              <i className="ri-alert-line mr-1 text-accent-600"></i>
              演示说明：本站播放器仅用于展示交互效果，暂无真实视频流，视频数据均为演示内容。
            </div>
          </div>
        </aside>
      </div>

      <RelatedVideos current={video} />

      {toast ? (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 shadow-sm">
          {toast}
        </div>
      ) : null}
    </div>
  );
}